import React, { useState, useEffect } from "react";
import {
  JemConfiguration,
  TenantResponseFromAPI,
  IUserProviderState,
  PageStyles,
  PageHeading,
  LoadingSpinner
} from "@jem/components";
import {
  getPostDetails,
  getValidateDetails,
  getPostDetailsFile,
  getValidateDetailsFile
} from "../../components/TestClientDashboard/TestPage.Request";
import {
  inputStyle,
  buttonStyle,
  containerStyle,
  preStyle,
  bodyFont,
  pageFont,
  headerFont,
  Mode,
  labelStyle
} from "../../components/TestClientDashboard/TestPage.style";
import { getTenantDetails } from "../../components/TenantDashboard/TenantDetails.Requests";
import RadioGroup from "../../components/TestClientDashboard/TestPage.types";
const TestDashboardPage = ({
  getTokenFn,
  Opsconfiguration,
  GLconfiguration
}: {
  getTokenFn: IUserProviderState["accessToken"];
  Opsconfiguration: JemConfiguration["OpsDashboardApi"];
  GLconfiguration: JemConfiguration["GeneralLedgerApi"];
}) => {
  const DEFAULT_MODE = Mode.Regular;
  const DEFAULT_DATA_TYPE = "Body";
  const [mode, setMode] = useState(DEFAULT_MODE);
  const [dataType, setDataType] = useState(DEFAULT_DATA_TYPE);
  const [tenants, setTenants] = useState<TenantResponseFromAPI[]>([]);
  const [fields, setFields] = useState([
    { name: "Batch File", value: "", selected: true, type: "file" },
    { name: "Payload", value: "", selected: false, type: "textarea" },
    { name: "Poster", value: "", selected: false, type: "text" }
  ]);
  const [regularFields, setRegularFields] = useState([
    { name: "Regular File", value: "", selected: true, type: "file" },
    { name: "JSON", value: "", selected: false, type: "textarea" }
  ]);
  const [apiResponse, setApiResponse] = useState<{ statusCode: number; error?: string; data?: any } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAppIds = async () => {
      if (!GLconfiguration) {
        return;
      }
      try {
        const token = await getTokenFn();
        if (token) {
          const tenantDetails = await getTenantDetails(GLconfiguration, getTokenFn);
          setTenants(tenantDetails);
        }
      } catch (error) {
        console.error("Error fetching tenant details:", error);
      }
    };
    fetchAppIds();
    setApiResponse(null);
  }, [GLconfiguration, getTokenFn, mode, dataType]);

  const handleModeChange = (newMode: string) => {
    setMode(newMode as Mode);
  };

  const handleCheckboxChange = (index: number) => {
    const updatedFields = fields.map((field, i) => (i === index ? { ...field, selected: !field.selected } : field));
    setFields(updatedFields);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement> | undefined, _fieldName: string) => {
    const file = event && event.target && event.target.files ? event.target.files[0] : null;
    if (file) {
      const updatedFields = fields.map((field) =>
        field.name === "Batch File" ? { ...field, value: file.name } : field
      );
      setFields(updatedFields);
    }
  };

  const updateField = (index: number, key: string, value: string) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, selected: !field.selected, [key]: value } : field
    );
    setFields(updatedFields);
  };

  const handleRegularFileChange = (e: React.ChangeEvent<HTMLInputElement>, _fieldName: string) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const setRegularFields = regularFields.map((field) =>
        field.name === "Batch File" ? { ...field, value: file.name } : field
      );
      setFields(setRegularFields);
    }
  };

  const updateRegularField = (index: number, key: string, value: string) => {
    const updateRegularFields = regularFields.map((field, i) =>
      i === index ? { ...field, selected: !field.selected, [key]: value } : field
    );
    setRegularFields(updateRegularFields);
  };

  const handlePost = async () => {
    await handleSubmit("post");
  };

  const handleValidate = async () => {
    await handleSubmit("validate");
  };

  const validateAppId = (): string | null => {
    const selectedAppId = (document.getElementById("appId") as HTMLSelectElement)?.value;
    if (!selectedAppId) {
      alert("Please select an App ID.");
      return null;
    }
    return selectedAppId;
  };

  const validateJson = (jsonPayload: string): boolean => {
    try {
      JSON.parse(jsonPayload);
      return true;
    } catch {
      alert("Invalid JSON format");
      return false;
    }
  };

  const getFile = (): File | null => {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    return fileInput?.files?.[0] || null;
  };

  const buildPayload = (mode: string, dataType: string): FormData | any | null => {
    const file = getFile();
    const jsonPayload = (document.querySelector("textarea") as HTMLTextAreaElement)?.value;
    if ((mode === Mode.Regular || mode === Mode.Batch) && dataType === "Body") {
      if (!file) {
        alert("Please select a batch file.");
        return null;
      }
      if (!validateJson(jsonPayload)) return null;
      const formData = new FormData();
      formData.append("attachment", file);
      formData.append("Payload", jsonPayload);

      return formData;
    }
    if ((mode === Mode.Regular || mode === Mode.Batch) && dataType === "Form") {
      if (!validateJson(jsonPayload)) return null;
      try {
        return JSON.parse(jsonPayload);
      } catch {
        alert("Invalid JSON format");
        return null;
      }
    }
    return null;
  };

  const makeApiCall = async (
    action: "post" | "validate",
    mode: string,
    dataType: string,
    selectedAppId: string,
    payload: any
  ) => {
    if (mode === Mode.Regular || mode === Mode.Batch) {
      if (dataType === "Body") {
        return action === "post"
          ? await getPostDetailsFile(Opsconfiguration, getTokenFn, selectedAppId, payload)
          : await getValidateDetailsFile(Opsconfiguration, getTokenFn, selectedAppId, payload);
      } else {
        return action === "post"
          ? await getPostDetails(Opsconfiguration, getTokenFn, selectedAppId, payload)
          : await getValidateDetails(Opsconfiguration, getTokenFn, selectedAppId, payload);
      }
    }
  };

  const handleSubmit = async (action: "post" | "validate") => {
    setIsLoading(true);
    setApiResponse(null);
    const selectedAppId = validateAppId();
    if (!selectedAppId) {
      setIsLoading(false);
      return;
    }
    const payload = buildPayload(mode, dataType);
    if (!payload) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await makeApiCall(action, mode, dataType, selectedAppId, payload);
      if (response) {
        setApiResponse({
          statusCode: response.statusCode || 404,
          data: JSON.stringify(response, null, 2)
        });
      }
    } catch (error: any) {
      setApiResponse({
        statusCode: error?.statusCode || 500,
        data: JSON.stringify({ error: error.message || "An unexpected error occurred." }, null, 2)
      });
    }
    setIsLoading(false);
  };

  const renderApiResponse = () => {
    try {
      let data = apiResponse?.data;
      if (typeof data === "string") {
        data = JSON.parse(data);
      }
      let innerData = data?.data;
      if (typeof innerData === "string") {
        try {
          innerData = JSON.parse(innerData);
        } catch {
          console.error("Error fetching tenant details:");
        }
      }
      return typeof innerData === "object" ? JSON.stringify(innerData, null, 2) : String(innerData);
    } catch (error) {
      return JSON.stringify({ error: "Failed to parse response", raw: apiResponse?.data }, null, 2);
    }
  };

  return (
    <div className={PageStyles.rootDiv}>
      <PageHeading>
        <h2>System-System(S2S) Platform Testing Page</h2>
      </PageHeading>
      <div style={pageFont}>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}
        >
          <div style={{ flex: 1, marginRight: "10px" }}>
            <label htmlFor="appId" style={headerFont}>
              App Id
            </label>
            <select
              className="ms-Grid-col ms-sm12 ms-md6 ms-lg3 ms-xl3 ms-xxl2"
              id="appId"
              style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
            >
              <option value="">Select</option>
              {tenants.map((tenant, index) => (
                <option key={index} value={tenant.appId}>
                  {tenant.name}-{tenant.appId}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, marginRight: "10px", padding: "28px" }}>
            <RadioGroup
              name="type"
              options={[Mode.Regular, Mode.Batch]}
              selectedValue={mode}
              onChange={handleModeChange}
              labelStyle={{ fontWeight: "normal", marginRight: "10px" }}
              disabledOptions={[Mode.Batch]}
            />
          </div>
          <div style={{ flex: 1, marginRight: "auto" }}>
            <label htmlFor="apiVersion" style={headerFont}>
              API Version
            </label>
            <div id="apiVersion" style={inputStyle}>
              v2.0
            </div>
          </div>
        </div>
        {mode === Mode.Regular && (
          <div style={{ marginBottom: "20px" }}>
            <div>
              <RadioGroup
                name="dataType"
                options={["Body", "Form"]}
                selectedValue={dataType}
                onChange={setDataType}
                labelStyle={{ fontWeight: "normal", marginRight: "10px" }}
              />
            </div>
            <br></br>
            {dataType === "Body" && (
              <div style={bodyFont}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
                  {regularFields.map((item, index) => (
                    <div
                      key={index}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
                    >
                      <div style={{ flex: 1 }}>
                        <label style={headerFont}>{item.name}</label>
                        {item.type === "file" ? (
                          <input
                            type="file"
                            style={inputStyle}
                            aria-label={item.name}
                            onChange={(e) => handleRegularFileChange(e, item.name)}
                          />
                        ) : item.type === "textarea" ? (
                          <textarea
                            style={inputStyle}
                            rows={15}
                            aria-label={item.name}
                            value={item.value}
                            onChange={(e) => updateRegularField(index, "value", e.target.value)}
                          />
                        ) : (
                          <input
                            type="text"
                            style={inputStyle}
                            aria-label={item.name}
                            value={item.value}
                            onChange={(e) => updateRegularField(index, "value", e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dataType === "Form" && (
              <div style={{ marginTop: "10px" }}>
                <label style={headerFont}>JSON</label>
                <textarea style={inputStyle} rows={15} aria-label="JSON"></textarea>
              </div>
            )}
          </div>
        )}
        {mode === Mode.Batch && (
          <div style={{ marginBottom: "20px" }}>
            <div>
              <RadioGroup
                name="dataType"
                options={["Body", "Form"]}
                selectedValue={dataType}
                onChange={setDataType}
                labelStyle={{ fontWeight: "normal", marginRight: "10px" }}
              />
            </div>
            <br></br>
            {dataType === "Body" && (
              <div style={bodyFont}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
                  {fields.map((item, index) => (
                    <div
                      key={index}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
                    >
                      <div style={{ flex: 1 }}>
                        <label style={headerFont}>{item.name}</label>
                        {item.type === "file" ? (
                          <input
                            type="file"
                            style={inputStyle}
                            aria-label={item.name}
                            onChange={(e) => handleFileChange(e, item.name)}
                          />
                        ) : item.type === "textarea" ? (
                          <textarea
                            style={inputStyle}
                            rows={10}
                            aria-label={item.name}
                            value={item.value}
                            onChange={(e) => updateField(index, "value", e.target.value)}
                          />
                        ) : (
                          <input
                            type="text"
                            style={inputStyle}
                            aria-label={item.name}
                            value={item.value}
                            onChange={(e) => updateField(index, "value", e.target.value)}
                          />
                        )}
                      </div>

                      <div>
                        <input type="checkbox" checked={item.selected} onChange={() => handleCheckboxChange(index)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dataType === "Form" && (
              <div style={{ marginTop: "10px" }}>
                <label style={headerFont}>JSON</label>
                <textarea style={inputStyle} rows={15} aria-label="JSON"></textarea>
              </div>
            )}
          </div>
        )}
        <div style={{ textAlign: "center" }}>
          <button onClick={handlePost} style={buttonStyle}>
            Post
          </button>
          <button onClick={handleValidate} style={buttonStyle}>
            Validate
          </button>
        </div>
        {isLoading && <LoadingSpinner label="Loading details" />}
        <div>
          <h3>API RESPONSE</h3>
          {apiResponse && (
            <div>
              <div style={containerStyle}>
                <b>Status Code:</b> {apiResponse.statusCode || "N/A"}
              </div>
              <b>API Response:</b>
              <div style={labelStyle}>
                <div style={preStyle}>{renderApiResponse()}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDashboardPage;
